import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { QueryClient, QueryClientProvider, keepPreviousData } from '@tanstack/react-query'
import { Toaster } from 'sonner'
import { PlatformLayout } from '@/components/layout/PlatformLayout'
import { NAV } from '@/components/platform/nav'
import {
  DashboardPage,
  AccountsPage,
  AccountDetailPage,
  TenantsPage,
  TenantDetailPage,
  NewTenantPage,
  PlansPage,
  SubscriptionsPage,
  SettingsPage,
  LoginPage,
  ProvisionsPage,
  ReleasesPage,
  StatsPage,
  InfraPage,
  CatalogsPage,
  AuditPage,
  WebAnalyticsPage,
  CouponsPage,
  NotificationsPage,
  NotificationComposerPage,
  NotificationDetailPage,
  ScoringPage,
  SegmentsPage,
  LeadsPage,
  ContactsPage,
  ContactDetailPage,
  InvoicesPage,
  InvoiceDetailPage,
  UsersPage,
  ProfilePage,
  SuperadminApprovalPage,
} from '@/pages'
import { PlaceholderPage } from '@/pages/PlaceholderPage'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 30_000,
      // Al cambiar filtros (queryKey nuevo) mantenemos la data anterior visible
      // hasta que llega la nueva, en vez de saltar a ceros/empty y volver. Sin flash.
      placeholderData: keepPreviousData,
    },
  },
})

// Pantallas ya migradas del prototipo Babel a la app Vite.
const MIGRATED_PATHS = new Set([
  '/dashboard',
  '/accounts',
  '/tenants',
  '/plans',
  '/subscriptions',
  '/settings',
  '/provisions',
  '/releases',
  '/stats',
  '/infra',
  '/catalogs',
  '/audit',
  '/web-analytics',
  '/coupons',
  '/notifications',
  '/scoring',
  '/segments',
  '/leads',
  '/contacts',
  '/invoices',
  '/users',
])

// El resto del nav todavía vive en el prototipo: mostramos PlaceholderPage
// hasta portar cada pantalla. Se derivan del NAV para no desincronizar.
const PLACEHOLDER_PATHS = NAV
  .map((item) => item.href)
  .filter((href) => !MIGRATED_PATHS.has(href))

// react-router necesita el basename del deploy (en preview puede ser /main/).
const basename = import.meta.env.BASE_URL.replace(/\/$/, '')

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter basename={basename}>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/superadmin-approval" element={<SuperadminApprovalPage />} />

          <Route element={<PlatformLayout />}>
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/accounts" element={<AccountsPage />} />
            <Route path="/accounts/:id" element={<AccountDetailPage />} />
            <Route path="/tenants" element={<TenantsPage />} />
            <Route path="/tenants/new" element={<NewTenantPage />} />
            <Route path="/tenants/:id" element={<TenantDetailPage />} />
            <Route path="/plans" element={<PlansPage />} />
            <Route path="/subscriptions" element={<SubscriptionsPage />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/provisions" element={<ProvisionsPage />} />
            <Route path="/releases" element={<ReleasesPage />} />
            <Route path="/stats" element={<StatsPage />} />
            <Route path="/infra" element={<InfraPage />} />
            <Route path="/catalogs" element={<CatalogsPage />} />
            <Route path="/audit" element={<AuditPage />} />
            <Route path="/web-analytics" element={<WebAnalyticsPage />} />
            <Route path="/coupons" element={<CouponsPage />} />
            <Route path="/notifications" element={<NotificationsPage />} />
            <Route path="/notifications/new" element={<NotificationComposerPage />} />
            <Route path="/notifications/:id" element={<NotificationDetailPage />} />
            <Route path="/scoring" element={<ScoringPage />} />
            <Route path="/segments" element={<SegmentsPage />} />
            <Route path="/leads" element={<LeadsPage />} />
            <Route path="/contacts" element={<ContactsPage />} />
            <Route path="/contacts/:id" element={<ContactDetailPage />} />
            <Route path="/invoices" element={<InvoicesPage />} />
            <Route path="/invoices/:id" element={<InvoiceDetailPage />} />
            <Route path="/users" element={<UsersPage />} />
            <Route path="/profile" element={<ProfilePage />} />
            {PLACEHOLDER_PATHS.map((path) => (
              <Route key={path} path={path} element={<PlaceholderPage />} />
            ))}
          </Route>

          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster richColors position="top-right" />
    </QueryClientProvider>
  )
}
